import { Format } from 'devextreme/localization';

import { TRowData } from './rowDefs';

/* ======================================== */

export const classNameDisabled = 'dx-disabled';

/* ======================================== */

/** define a cell value type */
export type TValue = string | number | boolean | Date | Record<string, unknown>;
type TValueType = 'string' | 'number' | 'boolean' | 'favourite' | 'date' | 'datetime' | 'object' | 'link';
type TAlignment = 'center' | 'left' | 'right';
type TSortDirection = 'asc' | 'desc';
type TSummaryType = 'avg' | 'count' | 'custom' | 'max' | 'min' | 'sum' | undefined;

/** define the base properties (column fields) of a row object - extend this (union) to include data-specific properties*/
export type TKey = 'id' | string;

/* ======================================== */

/** when a cell is edited the callback contains these parameters */
export type TCellValueChange = {
    value: TValue;
    previousValue: TValue;
    event: Event;
    element: HTMLElement;
}
export type TCellValueChangeCallback = (value: TValue, previousValue: TValue, event: Event, element: HTMLElement) => void;

/* -------------------- */

/** for the custom cell render callbacks, this is the template passed to the function */
export type TCellRenderTemplate = {
    readonly rowIndex: number;
    readonly key?: TKey;
    readonly data?: TRowData;
    readonly columnIndex: number;
    readonly column: TColumn;
    readonly cellElement: HTMLTableCellElement;
    readonly value: TValue;
}
export type TCellRenderTemplateCallback = (template: TCellRenderTemplate) => JSX.Element | string | undefined;

export type TCellRenderGroupHeaderTemplate = {
    readonly data: {
        readonly items: TRowData[];
    }
    readonly columnIndex: number;
    readonly column: TColumn;
    readonly value: TValue;
}
export type TCellRenderGroupHeaderTemplateCallback = (template: TCellRenderGroupHeaderTemplate) => JSX.Element | string | undefined;

/* -------------------- */

/** when calculating a cell value, this is the callback */
export type TCalculateCellValueCallback = (rowData: TRowData) => unknown;

/* -------------------- */

/** define the columns in a grid */
export type TColumn = {
    field: TKey;
    type?: TValueType;
    caption?: string;

    format?: Format;
    cssClass?: string;
    disabled?: boolean;
    alignment?: TAlignment;
    visible?: boolean;
    width?: number | string;
    minWidth?: number;

    allowSorting?: boolean;
    sortIndex?: number;
    sortOrder?: TSortDirection;
    calculateSortValue?: TCalculateCellValueCallback;
    sortingMethod?: (value1: unknown, value2: unknown) => number;

    // TODO to support groups (ie rows grouped by value in one column with group/tree expand / collapse)
    //    isDetail?: boolean;
    //    isValue?: boolean;
    groupIndex?: number;
    groupSummary?: TSummaryType;
    groupCellRender?: TCellRenderGroupHeaderTemplateCallback;
    autoExpandGroup?: boolean;
    totalSummary?: TSummaryType;

    isBand?: boolean;           // is this column a band that contains sub-columns
    ownerBandIndex?: number;    // this is a sub-column that belongs to a band column

    fixed?: boolean;
    hidingPriority?: number;

    allowEditing?: boolean;
    onCellValueChange?: TCellValueChangeCallback;
    onClick?: (rowData: TRowData) => void;

    headerCellRender?: TCellRenderTemplateCallback;
    cellRender?: TCellRenderTemplateCallback;
    cellRenderEdit?: TCellRenderTemplateCallback;
    calculateCellValue?: TCalculateCellValueCallback;

    xlFormat?: string;
};

export type TColumns = TColumn[] | undefined;

export type TColumnVisibility = {
    field: TKey | number;
    visible?: boolean;
}
